import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import type { FavoriteGroup } from '../../hooks/useFavoriteGroups';
import { useTranslation } from '../../i18n';

interface DeleteFavoriteGroupDialogProps {
  isOpen: boolean;
  group: FavoriteGroup | null;
  allGroups: FavoriteGroup[];
  onConfirm: (groupId: number | string, moveToGroupId: number | string | null) => void;
  onCancel: () => void;
}

export default function DeleteFavoriteGroupDialog({
  isOpen,
  group,
  allGroups,
  onConfirm,
  onCancel,
}: DeleteFavoriteGroupDialogProps) {
  const { t: strings } = useTranslation();
  const [moveToGroupId, setMoveToGroupId] = useState<string>('');

  const otherGroups = allGroups.filter((g) => g.id !== group?.id);
  const hasProducts = (group?.favorites?.length ?? 0) > 0;

  const handleConfirm = () => {
    if (!group) return;
    const targetGroupId = moveToGroupId || null;
    onConfirm(group.id, targetGroupId);
    setMoveToGroupId('');
  };

  const handleCancel = () => {
    setMoveToGroupId('');
    onCancel();
  };

  if (!group) return null;

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      title={strings.favorites.deleteGroup.title}
      message={strings.favorites.deleteGroup.message(group.name)}
      confirmLabel={strings.favorites.deleteGroup.confirm}
      cancelLabel={strings.favorites.deleteGroup.cancel}
      variant="danger"
      icon={<Trash2 className="w-6 h-6" />}
    >
      {hasProducts && otherGroups.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {strings.favorites.deleteGroup.moveLabel}
          </label>
          <select
            value={moveToGroupId}
            onChange={(e) => setMoveToGroupId(e.target.value)}
            className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:border-transparent transition-colors appearance-none cursor-pointer"
          >
            <option value="">
              {strings.favorites.deleteGroup.ungroupedOption}
            </option>
            {otherGroups.map((g) => (
              <option key={g.id} value={String(g.id)}>
                {g.name} ({g.favorites?.length ?? 0})
              </option>
            ))}
          </select>
        </div>
      )}
    </ConfirmDialog>
  );
}
